<script>
  import Layout from '../components/Layout.svelte';
  import { Heading } from '@button/ui';
  import { useQueryClient } from '@tanstack/svelte-query';
  import {
    QUERY_KEY_SHORTLINK_BULK_UPLOADS,
    QUERY_KEY_SHORTLINKS,
  } from '../constants';
  import { _ } from 'svelte-i18n';
  import Papa from 'papaparse';
  import { client } from '../lib/client';
  import { Button, Dropzone, A, Spinner, Card } from 'flowbite-svelte';
  import {
    FileCsvOutline,
    PlusSolid,
    CheckCircleOutline,
    ExclamationCircleSolid,
  } from 'flowbite-svelte-icons';
  import { sendBulkLinkCreatedDebugEvent } from '../data/debug';
  import { Link } from 'svelte-routing';
  const CSV_HEADERS = [
    'Link Destination',
    'Link Name',
    'Tag 1',
    'Tag 2',
    'Tag 3',
    'Tag 4',
    'Tag 5',
    'Tag 6',
    'Tag 7',
    'Tag 8',
    'Tag 9',
    'Tag 10',
  ];
  const CSV_FIELD_TO_API_MAPPING = {
    'Link Destination': 'url',
    'Link Name': 'name',
  };

  let file;
  let bulkLinkExportJobId = '';

  let hasData = false;
  let isLoading = false;
  let isError = false;

  function handleIsError(errorState) {
    isError = errorState;
  }
  function handleIsLoading(loadingState) {
    isLoading = loadingState;
  }
  function handleHasData(hasDataState) {
    hasData = hasDataState;
  }

  const validationErrors = {
    hasLinkIssue: false,
    hasFileIssue: false,
    hasUniquenessIssue: false,
  };

  function validateRow(row) {
    for (const csvField in row) {
      if (Object.prototype.hasOwnProperty.call(row, csvField)) {
        if (!CSV_HEADERS.includes(csvField.trim())) {
          validationErrors.hasFileIssue = true;
          return false;
        }
      }
    }
    if (!row['Link Destination']) {
      validationErrors.hasLinkIssue = true;
      return false;
    }
    try {
      // eslint-disable-next-line no-new
      new URL(row['Link Destination']);
    } catch (e) {
      validationErrors.hasLinkIssue = true;
      return false;
    }
    return true;
  }

  function transformRow(row) {
    const transformed = {};
    const tags = [];
    for (const csvField in row) {
      if (Object.prototype.hasOwnProperty.call(row, csvField)) {
        const rowValue = row[csvField].trim();
        if (CSV_FIELD_TO_API_MAPPING[csvField] && rowValue) {
          transformed[CSV_FIELD_TO_API_MAPPING[csvField]] = rowValue;
        } else if (csvField.startsWith('Tag') && rowValue) {
          tags.push(row[csvField]);
          transformed.tags = tags;
        }
      }
    }
    return transformed;
  }

  async function processCsv() {
    handleIsLoading(true);
    const fileRows = [];
    Papa.parse(file, {
      header: true,
      skipEmptyLines: 'greedy',
      step(row) {
        if (validateRow(row.data)) {
          const transformed = transformRow(row.data);
          fileRows.push(transformed);
        }
      },
      complete(results) {
        if (fileRows.length > 5000 || fileRows.length === 0) {
          validationErrors.hasFileIssue = true;
        }
        if (
          results.errors.length > 0 ||
          validationErrors.hasFileIssue ||
          validationErrors.hasLinkIssue
        ) {
          handleIsError(true);
          handleIsLoading(false);
        } else {
          onBulkCreateLinks(fileRows, file.name);
        }
      },
      // eslint-disable-next-line no-unused-vars
      error(error) {
        validationErrors.hasFileIssue = false;
        handleIsError(true);
      },
    });
  }
  const queryClient = useQueryClient();

  async function onBulkCreateLinks(fileRows, fileName) {
    const bulkLinkRequest = {
      rows: fileRows,
      name: fileName,
    };
    try {
      const response = await client.post(
        '/api/bulk-create-shortlink',
        bulkLinkRequest,
      );

      if (response.status === 201) {
        const shortlinkBulkUploadId =
          response.data.object.shortlink_bulk_upload_id;
        bulkLinkExportJobId = shortlinkBulkUploadId;
        handleIsLoading(false);
        handleHasData(true);
        sendBulkLinkCreatedDebugEvent(fileRows.length, shortlinkBulkUploadId);
        await queryClient.invalidateQueries([
          QUERY_KEY_SHORTLINK_BULK_UPLOADS,
          QUERY_KEY_SHORTLINKS,
        ]);
        return;
      }
      return;
    } catch (error) {
      if (error.response.status === 400) {
        validationErrors.hasUniquenessIssue = true;
      }
      handleIsError(true);
      handleIsLoading(false);
    }
    handleIsError(true);
    handleIsLoading(false);
  }

  async function handleFileChange(event) {
    [file] = event.target.files;
    await processCsv();
  }

  function handleUploadSpreadsheetButton() {
    document.getElementById('fileInput').click();
  }

  function downloadExampleCSV() {
    const csvExampleRow =
      'https://www.amazon.com/dp/B079VP6DH5/,,my-tag-1, my-tag-2,,,,,,,,';
    const csvExampleRow2 =
      'https://www.amazon.com/dp/B09N9WGLTC/,,my-tag-1, my-tag-2,,,,,,,,';
    const csvExample = [CSV_HEADERS, csvExampleRow, csvExampleRow2].join('\n');
    const blob = new Blob([csvExample], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = 'example_button_links.csv';
    link.click();
    window.URL.revokeObjectURL(link.href);
  }

  const dropHandle = async (event) => {
    event.preventDefault();
    if (event.dataTransfer.items.length === 1) {
      const item = event.dataTransfer.items[0];
      if (item.kind === 'file') {
        const itemFile = item.getAsFile();
        if (itemFile && itemFile.type !== 'text/csv') {
          validationErrors.hasFileIssue = true;
          handleIsError(true);
        } else {
          file = itemFile;
          await processCsv();
        }
      }
    }
  };

  function handleClickExport(event) {
    window.location = `/export-csv?shortlinkBulkUploadId=${bulkLinkExportJobId}`;
    event.target.blur();
  }

  function handleReset() {
    bulkLinkExportJobId = '';
    validationErrors.hasLinkIssue = false;
    validationErrors.hasFileIssue = false;
    validationErrors.hasUniquenessIssue = false;
    hasData = false;
    isLoading = false;
    isError = false;
  }
</script>

<Layout>
  <Card class="mb-4 w-full max-w-none">
    <Heading class="mb-4 break-words"
      >{$_('bulkLinks.header', {
        default: 'Bulk Link Creator',
      })}</Heading
    >
    <Dropzone
      class="mb-4 h-3/5 w-full"
      accept=".csv"
      maxSize="1"
      on:drop={dropHandle}
      on:dragover={(event) => {
        event.preventDefault();
      }}
    >
      {#if !hasData && !isError && !isLoading}
        <FileCsvOutline color="green" class="mb-4 mt-8 h-20 w-1/2" />
        <Heading class="mb-4 break-words"
          >{$_('bulkLinks.initial.uploadLinksMessage', {
            default: 'Create up to 5000 shortlinks via spreadsheet',
          })}</Heading
        >
        <p class="mb-4 text-gray-500">
          <span class="mr-1 text-gray-500">✔️</span>
          {$_('bulkLinks.initial.columns', {
            default:
              'Supported Columns: Link Destination, Link Name, Up to 10 columns for Tags (Tag 1, Tag 2,...,Tag 10)',
          })}
        </p>
        <p class="mb-4 flex items-center text-gray-500">
          <span class="mr-1 text-gray-500">✔️</span>
          {$_('bulkLinks.initial.oneLinkPerRow', {
            default: 'CSV must contain one link per row',
          })}
        </p>
        <p class="mb-4 flex items-center text-gray-500">
          <span class="mr-1 text-gray-500">✔️</span>
          {$_('bulkLinks.initial.optionalFields', {
            default: 'Link Names and Tags are optional',
          })}
        </p>
        <p class="mb-4 flex items-center text-gray-500">
          <span class="mr-1 text-gray-500">✔️</span>
          {$_('bulkLinks.initial.uniqueFields', {
            default: 'Link Names and CSV file names must be unique',
          })}
        </p>
        <div>
          <Button on:click={handleUploadSpreadsheetButton}>
            <PlusSolid size="sm" class="mr-2" />
            {$_('bulkLinks.cta.uploadSpreadsheet', {
              default: 'Upload a Spreadsheet',
            })}
            <input
              id="fileInput"
              type="file"
              accept=".csv"
              class="hidden"
              on:change={handleFileChange}
            />
          </Button>
        </div>
        <A
          class="font-xs mb-4 mt-6 text-xs hover:underline"
          on:click={downloadExampleCSV}
        >
          {$_('bulkLinks.cta.downloadExampleCSV', {
            default: 'Download Example CSV',
          })}</A
        >
      {/if}
      {#if isLoading}
        <div class="relative">
          <div class="h-full w-full items-center justify-center">
            <Spinner class="mb-4 mt-8 h-20 w-1/2" />
            <Heading class="mb-4 break-words">Uploading</Heading>
            <p class="mb-4 text-blue-500">
              {$_('bulkLinks.uploading.doNotRefresh', {
                default:
                  'Please do not refresh or navigate away from this page until complete.',
              })}
            </p>
          </div>
        </div>
      {/if}
      {#if isError}
        <ExclamationCircleSolid color="red" class="mb-4 mt-8 h-20 w-1/2" />
        {#if validationErrors.hasLinkIssue}
          <p class="mb-4 text-lg font-extrabold text-red-500">
            {$_('bulkLinks.errors.invalidLinks', {
              default:
                'Invalid links found. Please ensure all of the below are true.',
            })}
          </p>
          <p class="mb-2 text-gray-300">
            {$_('bulkLinks.errors.urlPresent', {
              default: 'Url must be present on each row.',
            })}
          </p>
          <p class="mb-2 text-gray-300">
            {$_('bulkLinks.errors.validUrls', {
              default: 'All urls must be valid.',
            })}
          </p>
        {:else if validationErrors.hasUniquenessIssue}
          <p class="mb-4 text-lg font-extrabold text-red-500">
            {$_('bulkLinks.errors.duplicate', {
              default: 'Duplicates found.',
            })}
          </p>
          <p class="mb-2 text-gray-300">
            {$_('bulkLinks.errors.duplicateDetails', {
              default:
                'Please ensure that the file name and all provided Link Names are unique.',
            })}
          </p>
        {:else if validationErrors.hasFileIssue}
          <p class="mb-4 text-lg font-extrabold text-red-500">
            {$_('bulkLinks.errors.fileIssues.title', {
              default:
                'File issues found. Please ensure all of the below are true.',
            })}
          </p>
          <p class="mb-2 text-gray-300">
            {$_('bulkLinks.errors.fileIssues.format', {
              default: 'File must have proper headers. See example for format.',
            })}
          </p>
          <p class="mb-2 text-gray-300">
            {$_('bulkLinks.errors.fileIssues.type', {
              default: 'File must be a csv.',
            })}
          </p>
          <p class="mb-2 text-gray-300">
            {$_('bulkLinks.errors.fileIssues.max', {
              default: 'File cannot have more than 5000 links.',
            })}
          </p>
          <A
            class="font-xs mb-4 mt-6 text-xs hover:underline"
            on:click={downloadExampleCSV}
          >
            {$_('bulkLinks.cta.fileIssues.downloadExampleCSV', {
              default: 'Download Example CSV',
            })}</A
          >
        {:else}
          <p class="mb-4 text-lg font-extrabold text-red-500">
            {$_('bulkLinks.errors.somethingWentWrong', {
              default: 'Something went wrong.',
            })}
          </p>
        {/if}
        <Button
          on:click={handleReset}
          size="sm"
          color="blue"
          class="mb-4 mt-2 w-fit"
          >{$_('bulkLinks.tryagain', {
            default: 'Try Again',
          })}</Button
        >
      {/if}
      {#if hasData && !isError && !isLoading}
        <CheckCircleOutline color="green" class="mb-4 mt-8 h-20 w-1/2"
        ></CheckCircleOutline>
        <p class="mb-4 text-lg font-extrabold text-green-700">
          {$_('bulkLinks.success', {
            default: 'The links were successfully uploaded.',
          })}
        </p>
        <p class="mb-4 text-gray-500">
          {$_('bulkLinks.success.details', {
            default:
              'You can export links or visit the My Links page to view and copy your links.',
          })}
        </p>
        <div class="flex gap-4">
          <Button
            on:click={handleClickExport}
            size="sm"
            color="blue"
            class="w-fit"
            >{$_('bulkLinks.cta.export', { default: 'Export' })}</Button
          >
          <Link
            to={`/?scrollTo=shortlinks&shortlinkBulkUploadId=${bulkLinkExportJobId}`}
          >
            <Button color="light" size="sm">
              {$_('bulkLinks.cta.myLinks', {
                default: 'Go to My Links',
              })}</Button
            >
          </Link>
        </div>
        <div class="mb-4 mt-4 flex w-full justify-center">
          <Button on:click={handleReset} size="sm" color="blue"
            >{$_('bulkLinks.createMoreLinks', {
              default: 'Create More Links',
            })}
          </Button>
        </div>
      {/if}
    </Dropzone>
  </Card>
</Layout>
